import React from "react";
import { AlertTriangle } from "lucide-react";

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
    backgroundColor: "#f9fafb",
  },
  content: {
    maxWidth: "500px",
    textAlign: "center",
    padding: "2rem",
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
  icon: {
    width: "4rem",
    height: "4rem",
    color: "#ef4444",
    margin: "0 auto 1rem",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: "700",
    color: "#111827",
    marginBottom: "0.5rem",
  },
  message: {
    fontSize: "1rem",
    color: "#6b7280",
    marginBottom: "1.5rem",
    lineHeight: "1.5",
  },
  details: {
    marginBottom: "1.5rem",
    textAlign: "left",
  },
  summary: {
    cursor: "pointer",
    fontSize: "0.875rem",
    color: "#9ca3af",
    marginBottom: "0.5rem",
    userSelect: "none",
  },
  stack: {
    fontFamily: "monospace",
    fontSize: "0.75rem",
    backgroundColor: "#f3f4f6",
    padding: "1rem",
    borderRadius: "8px",
    overflow: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: "#6b7280",
    maxHeight: "200px",
  },
  actions: {
    display: "flex",
    gap: "0.75rem",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  button: {
    padding: "0.5rem 1rem",
    fontSize: "0.875rem",
    fontWeight: "500",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  primaryButton: {
    backgroundColor: "#3b82f6",
    color: "white",
  },
  secondaryButton: {
    backgroundColor: "#e5e7eb",
    color: "#374151",
  },
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.content}>
            <AlertTriangle style={styles.icon} />
            <h1 style={styles.title}>Algo salió mal</h1>
            <p style={styles.message}>
              Ha ocurrido un error inesperado. Por favor, intenta recargar la
              página.
            </p>
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details style={styles.details}>
                <summary style={styles.summary}>
                  Detalles del error (solo en desarrollo)
                </summary>
                <pre style={styles.stack}>
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            <div style={styles.actions}>
              <button
                onClick={() => window.location.reload()}
                style={{ ...styles.button, ...styles.primaryButton }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#2563eb")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "#3b82f6")
                }
              >
                Recargar página
              </button>
              <button
                onClick={this.handleReset}
                style={{ ...styles.button, ...styles.secondaryButton }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#d1d5db")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "#e5e7eb")
                }
              >
                Intentar de nuevo
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
